import * as React from 'react';
import { globalMessage } from '@/utils/globalMessage';
import { FetchSummaryViewParams } from '@/types/summaryView';

const MAX_BATCH_RETRIES = 1;

// --- State Types ---

interface IdleState {
    status: 'idle';
}

interface ProcessingState {
    status: 'processing';
    queue: FetchSummaryViewParams[];
    currentIndex: number;
    retryCount: number;
}

interface PaginatingState {
    status: 'paginating';
}

interface ErrorState {
    status: 'error';
}

type BatchState = IdleState | ProcessingState | PaginatingState | ErrorState;

// --- Action Types ---

type BatchAction =
    | { type: 'START_BATCH'; batches: FetchSummaryViewParams[] }
    | { type: 'START_SINGLE_FETCH' }
    | { type: 'START_PAGINATION' }
    | { type: 'FETCH_COMPLETE_SUCCESS' }
    | { type: 'FETCH_COMPLETE_ERROR'; error: string }
    | { type: 'RESET' };

// --- Reducer ---

function batchReducer(state: BatchState, action: BatchAction): BatchState {
    switch (action.type) {
        case 'START_BATCH':
            return {
                status: 'processing',
                queue: action.batches,
                currentIndex: 0,
                retryCount: 0,
            };

        case 'START_SINGLE_FETCH':
            return { status: 'idle' };

        case 'START_PAGINATION':
            return { status: 'paginating' };

        case 'FETCH_COMPLETE_SUCCESS':
            if (state.status === 'processing') {
                const nextIndex = state.currentIndex + 1;
                if (nextIndex >= state.queue.length) {
                    // All batches complete
                    return { status: 'idle' };
                }
                return {
                    ...state,
                    currentIndex: nextIndex,
                    retryCount: 0,
                };
            }
            return { status: 'idle' };

        case 'FETCH_COMPLETE_ERROR':
            if (state.status === 'processing') {
                if (state.retryCount < MAX_BATCH_RETRIES) {
                    return {
                        ...state,
                        retryCount: state.retryCount + 1,
                    };
                }
                const nextIndex = state.currentIndex + 1;
                if (nextIndex >= state.queue.length) {
                    return { status: 'idle' };
                }
                return {
                    ...state,
                    currentIndex: nextIndex,
                    retryCount: 0,
                };
            }
            if (state.status === 'paginating') {
                return { status: 'idle' };
            }
            return { status: 'error' };

        case 'RESET':
            return { status: 'idle' };

        default:
            return state;
    }
}

interface UseBatchFetchingParams {
    fetchSummaryView: (params: FetchSummaryViewParams) => void;
    destinationLimit: number;
    isLoadingSummaryView: boolean;
    fetchError: string | null;
}

interface UseBatchFetchingResult {
    // Current batch state
    batchState: BatchState;
    // Start a batched fetch (will split into batches if needed)
    startBatchFetch: (params: FetchSummaryViewParams) => void;
    // Start a pagination fetch (single page load)
    startPaginationFetch: (params: FetchSummaryViewParams) => void;
    resetError: () => void;
    isBatchProcessing: boolean;
    isPaginating: boolean;
    hasError: boolean;
}

export const useBatchFetching = ({
    fetchSummaryView,
    destinationLimit,
    isLoadingSummaryView,
    fetchError,
}: UseBatchFetchingParams): UseBatchFetchingResult => {
    const [batchState, dispatch] = React.useReducer(batchReducer, { status: 'idle' });

    const pendingFetchRef = React.useRef(false);

    const prevLoadingRef = React.useRef(isLoadingSummaryView);

    React.useEffect(() => {
        const wasLoading = prevLoadingRef.current;
        const isLoading = isLoadingSummaryView;

        if (wasLoading !== isLoading) {
            prevLoadingRef.current = isLoading;
        }

        if (wasLoading && !isLoading && pendingFetchRef.current) {
            pendingFetchRef.current = false;

            if (fetchError) {
                if (batchState.status === 'processing') {
                    if (batchState.retryCount >= MAX_BATCH_RETRIES) {
                        globalMessage.error(fetchError || 'Failed to load some destinations');
                    }
                } else if (batchState.status === 'paginating') {
                    globalMessage.error(fetchError || 'Failed to load more destinations');
                }
                dispatch({ type: 'FETCH_COMPLETE_ERROR', error: fetchError });
            } else {
                dispatch({ type: 'FETCH_COMPLETE_SUCCESS' });
            }
        }
    }, [isLoadingSummaryView, fetchError, batchState]);

    React.useEffect(() => {
        if (isLoadingSummaryView || pendingFetchRef.current) {
            return;
        }

        if (batchState.status === 'processing') {
            const currentBatch = batchState.queue[batchState.currentIndex];
            if (currentBatch) {
                pendingFetchRef.current = true;
                fetchSummaryView(currentBatch);
            }
        }
    }, [batchState, isLoadingSummaryView, fetchSummaryView]);

    const startBatchFetch = React.useCallback(
        (params: FetchSummaryViewParams) => {
            const limit = destinationLimit || 20;

            // If small enough, just do a single fetch (no batch state needed)
            if (params.destinationIds.length <= limit) {
                pendingFetchRef.current = true;
                dispatch({ type: 'START_SINGLE_FETCH' });
                fetchSummaryView(params);
                return;
            }

            // Split into batches
            const batches: FetchSummaryViewParams[] = [];
            for (let i = 0; i < params.destinationIds.length; i += limit) {
                batches.push({
                    destinationIds: params.destinationIds.slice(i, i + limit),
                    versions: params.versions,
                    properties: params.properties,
                });
            }

            // Start batch processing - the effect will trigger the first fetch
            dispatch({ type: 'START_BATCH', batches });
        },
        [destinationLimit, fetchSummaryView],
    );

    const startPaginationFetch = React.useCallback(
        (params: FetchSummaryViewParams) => {
            pendingFetchRef.current = true;
            dispatch({ type: 'START_PAGINATION' });
            fetchSummaryView(params);
        },
        [fetchSummaryView],
    );

    const resetError = React.useCallback(() => {
        dispatch({ type: 'RESET' });
    }, []);

    return {
        batchState,
        startBatchFetch,
        startPaginationFetch,
        resetError,
        isBatchProcessing: batchState.status === 'processing',
        isPaginating: batchState.status === 'paginating',
        hasError: batchState.status === 'error',
    };
};
